import React from 'react'
import { Box, Container, Grid } from '@mui/material'

import type { HolidayNavTab, HolidayPackage } from '@/src/types/holidays'
import HeaderContainer from '@/src/components/common/header/HeaderRouter'
import HolidaysFooter from '@/src/components/desktop/homepageFooter'
import ErrorBoundaryWrapper from '@/src/containers/common/ErrorBoundary'
import { HOLIDAYS_MAX_WIDTH } from '@/src/utils/holidays'

import HolidayBreadcrumbsWrapper from '@/src/components/common/holidays/holidayBreadcrumbs'
import PackageHeroWrapper from '@/src/components/common/holidays/packageHero'
import PackageSummaryWrapper from '@/src/components/common/holidays/packageSummary'
import HolidayNavTabsWrapper from '@/src/components/common/holidays/holidayNavTabs'
import ContactStripWrapper from '@/src/components/common/holidays/contactStrip'
import BeforeYouBookWrapper from '@/src/components/common/holidays/beforeYouBook'
import PackageInclusionsWrapper from '@/src/components/common/holidays/packageInclusions'
import HolidaySection from '@/src/components/common/holidays/holidaySection/holidaySection'
import type { HolidayPageBlock } from '@/src/utils/holidays/buildHolidayPageBlocks'
import EnquiryPopupWrapper from '@/src/components/common/holidays/enquiryPopup'
import HolidaySectionWrapper from '@/src/components/common/holidays/holidaySection'
import EnquiryCardWrapper from '@/src/components/common/holidays/enquiryCard'
import type { RefundTimelineSource } from '@/src/utils/holidays/parseRefundTimeline'

interface HolidayPackageDesktopPageProps {
    holidayPackage: HolidayPackage
    blocks: HolidayPageBlock[]
    navTabs: HolidayNavTab[]
    collectionLabel: string
    domainCountry: string
    /** The one refund schedule for this package, and the section it belongs to. */
    refundTimeline: RefundTimelineSource | null
}

const HolidayPackageDesktopPage: React.FC<HolidayPackageDesktopPageProps> = ({
    holidayPackage,
    blocks,
    navTabs,
    collectionLabel,
    refundTimeline,
    domainCountry
}) => {
    return (
        <Box
            sx={{
                minHeight: '100vh',
                bgcolor: 'background.default',
                display: 'flex',
                flexDirection: 'column',
                // `clip` not `hidden`: overflow-x:hidden makes this a scroll container, which
                // silently disables position:sticky for every descendant — including the enquiry
                // card. `clip` trims the same overflow without creating that container.
                overflowX: 'clip'
            }}
        >
            <ErrorBoundaryWrapper identifier="holidays-package-header-desktop">
                <HeaderContainer domainCountry={domainCountry} activeVertical="holidays" />
            </ErrorBoundaryWrapper>

            {/*
             * The package names itself before it shows itself: breadcrumb, title and the facts a
             * reader checks first, then the gallery. The photos led the page before, which meant
             * the name of the thing being sold arrived after a screenful of scenery.
             */}
            <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, pt: 2.5 }}>
                <ErrorBoundaryWrapper identifier="holidays-package-breadcrumbs-desktop">
                    <Box sx={{ pb: 1.5 }}>
                        <HolidayBreadcrumbsWrapper
                            collection={{ slug: holidayPackage.collectionSlug, label: collectionLabel }}
                            currentLabel={holidayPackage.summary.destinationName}
                        />
                    </Box>
                </ErrorBoundaryWrapper>

                <ErrorBoundaryWrapper identifier="holidays-package-summary-desktop">
                    <Box sx={{ pb: 2.5 }}>
                        <PackageSummaryWrapper summary={holidayPackage.summary} isMobile={false} />
                    </Box>
                </ErrorBoundaryWrapper>

                <ErrorBoundaryWrapper identifier="holidays-package-hero-desktop">
                    <PackageHeroWrapper gallery={holidayPackage.gallery} isMobile={false} />
                </ErrorBoundaryWrapper>
            </Container>

            {/* The tabs pin to the top on their own now that the breadcrumb sits above the hero. */}
            <Box
                sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 11,
                    bgcolor: 'background.default',
                    py: 1.5,
                    mt: 2
                }}
            >
                <Container maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH }}>
                    <ErrorBoundaryWrapper identifier="holidays-package-nav-desktop">
                        <HolidayNavTabsWrapper tabs={navTabs} isMobile={false} />
                    </ErrorBoundaryWrapper>
                </Container>
            </Box>

            <Container component="main" maxWidth={false} sx={{ maxWidth: HOLIDAYS_MAX_WIDTH, pb: 4 }}>
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 8 }}>
                        {/* Reaches the reader before the package itself, under the section tabs. */}
                        <Box sx={{ mb: 2 }}>
                            <ErrorBoundaryWrapper identifier="holidays-package-contact-top-desktop">
                                <ContactStripWrapper layout="inline" isMobile={false} />
                            </ErrorBoundaryWrapper>
                        </Box>

                        {/*
                         * The blocks sit on the page, not in a card: each one already draws its
                         * own structure — day cards, tabbed panels, tables — and a card around all
                         * of them added a second frame around every one of those.
                         */}
                        <Box>
                            {blocks.map((block, index) => (
                                <ErrorBoundaryWrapper
                                    key={block.id}
                                    identifier={`holidays-package-block-${block.id}-desktop`}
                                >
                                    {block.kind === 'section' ? (
                                        <HolidaySectionWrapper
                                            section={block.sections[0]}
                                            refundBands={
                                                refundTimeline?.sectionId === block.sections[0].id
                                                    ? refundTimeline.bands
                                                    : undefined
                                            }
                                            refundReplacesBody={refundTimeline?.origin === 'cancellation'}
                                            collectionSlug={holidayPackage.collectionSlug}
                                            variant="flat"
                                            isFirst={index === 0}
                                            isMobile={false}
                                        />
                                    ) : (
                                        // A grouped block owns the heading its sections used to
                                        // carry, so it borrows the same shell for id and chrome.
                                        <HolidaySection
                                            id={block.id}
                                            title={block.title}
                                            variant="flat"
                                            isFirst={index === 0}
                                            isMobile={false}
                                        >
                                            {block.kind === 'inclusions' ? (
                                                <PackageInclusionsWrapper
                                                    sections={block.sections}
                                                    isMobile={false}
                                                />
                                            ) : (
                                                <BeforeYouBookWrapper
                                                    sections={block.sections}
                                                    collectionSlug={holidayPackage.collectionSlug}
                                                    refundTimeline={refundTimeline ?? undefined}
                                                    isMobile={false}
                                                />
                                            )}
                                        </HolidaySection>
                                    )}
                                </ErrorBoundaryWrapper>
                            ))}
                        </Box>

                        <Box sx={{ mt: 2 }}>
                            <ErrorBoundaryWrapper identifier="holidays-package-contact-bottom-desktop">
                                <ContactStripWrapper layout="inline" isMobile={false} />
                            </ErrorBoundaryWrapper>
                        </Box>
                    </Grid>

                    {/*
                     * alignSelf stretch is what makes the sticky card work: the grid row is as tall
                     * as the content column, and a sticky child can only travel inside its own
                     * parent's box. Without it the column collapses to the card's height and the
                     * card scrolls away.
                     */}
                    <Grid size={{ xs: 12, md: 4 }} sx={{ alignSelf: 'stretch' }}>
                        <Box sx={{ position: 'sticky', top: 136 }}>
                            <ErrorBoundaryWrapper identifier="holidays-package-enquiry-desktop">
                                <EnquiryCardWrapper
                                    enquiry={holidayPackage.enquiry}
                                    startingPrice={holidayPackage.summary.startingPrice}
                                    isMobile={false}
                                />
                            </ErrorBoundaryWrapper>
                        </Box>
                    </Grid>
                </Grid>
            </Container>

            <Box sx={{ mt: 'auto' }}>
                <HolidaysFooter showHomeHighlights={false} />
            </Box>

            <ErrorBoundaryWrapper identifier="holidays-package-enquirypopup-desktop">
                <EnquiryPopupWrapper
                    enquiry={holidayPackage.enquiry}
                    startingPrice={holidayPackage.summary.startingPrice}
                    isMobile={false}
                />
            </ErrorBoundaryWrapper>
        </Box>
    )
}

export { HolidayPackageDesktopPage }
export default HolidayPackageDesktopPage
